import { useEffect, useState } from "react";
import { useLocale } from "@hooks/useLocale";
import { InlineRowGrid, type InlineColumn } from "@components/input";
import { useUnits } from "@units/useUnits";
import { fmtNum } from "@lib/format";
import type { MudCheckOut } from "@api/types";

/** Draft shape for a new property check (entry row). */
interface CheckDraft {
  check_time: string;
  check_depth: string;
  mw: string;
  pv: string;
  yp: string;
  ph: string;
  chlorides: string;
}

const emptyDraft = (): CheckDraft => ({
  check_time: "",
  check_depth: "",
  mw: "",
  pv: "",
  yp: "",
  ph: "",
  chlorides: "",
});

/**
 * Properties section (§8) — the day's mud-check grid. Column-driven via the
 * shared InlineRowGrid: the bottom row IS the add mechanism. Committed rows are
 * optimistic-local until the write endpoint (POST/PATCH …/mud, §10.4-b) is
 * wired; the fetched rows are the source of truth. Depth is rendered through the
 * report's unit profile so it labels ft/m dynamically (never a locale guess).
 */
export function MudProperties({
  checks,
  reportDate,
  unitProfileKey,
  readOnly = false,
}: {
  checks: MudCheckOut[];
  reportDate?: string;
  unitProfileKey?: string | null;
  readOnly?: boolean;
}) {
  const { t, locale } = useLocale();
  const { convertField, fmt } = useUnits(unitProfileKey);

  // Optimistic local additions layered over the served rows.
  const [added, setAdded] = useState<MudCheckOut[]>([]);
  useEffect(() => setAdded([]), [checks]);
  const rows = [...checks, ...added];

  const depthUnit = convertField("mud_check", "check_depth", 0, unitProfileKey).unit ?? "";
  const depthHeader = depthUnit ? `${t("mud.col.depth")} (${depthUnit})` : t("mud.col.depth");

  const num = (v: number | null | undefined, digits = 1) =>
    v == null ? t("common.na") : fmtNum(v, locale, { maximumFractionDigits: digits });

  const columns: InlineColumn<MudCheckOut, CheckDraft>[] = [
    {
      key: "time",
      header: t("mud.col.time"),
      render: (r) => r.check_time ?? t("common.na"),
      input: (d, patch) => (
        <input
          type="time"
          aria-label={t("mud.col.time")}
          value={d.check_time}
          onChange={(e) => patch({ check_time: e.target.value })}
        />
      ),
    },
    {
      key: "depth",
      header: depthHeader,
      num: true,
      render: (r) =>
        fmt(convertField("mud_check", "check_depth", r.check_depth, unitProfileKey), {
          maximumFractionDigits: 0,
        }),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={depthHeader}
          value={d.check_depth}
          onChange={(e) => patch({ check_depth: e.target.value })}
        />
      ),
    },
    numCol("mw", `${t("mud.col.mw")} (ppg)`, (r) => r.mw, 2),
    numCol("pv", `${t("mud.col.pv")} (cP)`, (r) => r.pv, 0),
    numCol("yp", `${t("mud.col.yp")} (lbf/100ft²)`, (r) => r.yp, 0),
    numCol("ph", t("mud.col.ph"), (r) => r.ph, 1),
    numCol("chlorides", `${t("mud.col.chlorides")} (mg/L)`, (r) => r.chlorides, 0),
  ];

  // Build numeric columns with a shared render/input factory.
  function numCol(
    key: keyof CheckDraft,
    header: string,
    get: (r: MudCheckOut) => number | null,
    digits: number,
  ): InlineColumn<MudCheckOut, CheckDraft> {
    return {
      key,
      header,
      num: true,
      render: (r) => num(get(r), digits),
      input: (d, patch) => (
        <input
          type="number"
          inputMode="decimal"
          aria-label={header}
          value={d[key]}
          onChange={(e) => patch({ [key]: e.target.value } as Partial<CheckDraft>)}
        />
      ),
    };
  }

  const toRow = (d: CheckDraft): MudCheckOut | null => {
    // Require at least MW — the anchor property of a check.
    if (!d.mw.trim()) return null;
    const n = (s: string) => (s.trim() ? Number(s) : null);
    return {
      id: `mc-local-${Date.now()}`,
      report_id: "",
      event_id: "",
      check_time: d.check_time || null,
      check_depth: n(d.check_depth),
      mw: n(d.mw),
      pv: n(d.pv),
      yp: n(d.yp),
      ph: n(d.ph),
      chlorides: n(d.chlorides),
      carry_over: false,
    };
  };

  return (
    <section className="card">
      <div className="panel-head-row">
        <h3>
          {t("mud.section.properties")}{" "}
          <span className="dim" style={{ fontWeight: 400, fontSize: 12 }}>
            — {t("mud.section.properties.note")}
          </span>
        </h3>
      </div>
      <InlineRowGrid<MudCheckOut, CheckDraft>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        emptyDraft={emptyDraft}
        toRow={toRow}
        onCommit={(row) => setAdded((a) => [...a, row])}
        isCarried={(r) => r.carry_over}
        rowOwner={() => "p"}
        readOnly={readOnly}
        ariaLabel={t("mud.section.properties")}
        emptyLabel={t("mud.noChecks")}
      />
      {reportDate ? (
        <p className="hint" style={{ marginTop: 6 }}>
          {t("mud.selectReport")}: {reportDate}
        </p>
      ) : null}
    </section>
  );
}
